import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Input } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowDown, ArrowUp, Search, Shield, Star } from 'lucide-react';
import { memo, useMemo, useState } from 'react';
const clearanceLabels = {
    public_trust: 'Public Trust',
    secret: 'Secret',
    top_secret: 'Top Secret',
    ts_sci: 'TS/SCI'
};
const clearanceColors = {
    public_trust: 'bg-blue-100 text-blue-700',
    secret: 'bg-yellow-100 text-yellow-700',
    top_secret: 'bg-orange-100 text-orange-700',
    ts_sci: 'bg-red-100 text-red-700'
};
export const LCATsTable = /*#__PURE__*/ memo(function LCATsTable({ lcats, onRowClick }) {
    const [sortField, setSortField] = useState('clin');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filters, setFilters] = useState({
        search: '',
        category: 'all',
        team: 'all'
    });
    // Get unique categories for filter
    const categories = useMemo(()=>{
        const uniqueCategories = new Set(lcats.map((lcat)=>lcat.category).filter(Boolean));
        return Array.from(uniqueCategories).sort();
    }, [
        lcats
    ]);
    // Get unique teams for filter
    const teams = useMemo(()=>{
        const uniqueTeams = new Set(lcats.map((lcat)=>lcat.teamName).filter(Boolean));
        return Array.from(uniqueTeams).sort();
    }, [
        lcats
    ]);
    // Handle sort
    const handleSort = (field)=>{
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };
    // Filter and sort LCATs
    const filteredAndSortedLCATs = useMemo(()=>{
        let result = [
            ...lcats
        ];
        // Apply filters
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            result = result.filter((lcat)=>lcat.code?.toLowerCase().includes(searchLower) || lcat.title?.toLowerCase().includes(searchLower) || lcat.category?.toLowerCase().includes(searchLower) || lcat.description?.toLowerCase().includes(searchLower));
        }
        if (filters.category !== 'all') {
            result = result.filter((lcat)=>lcat.category === filters.category);
        }
        if (filters.team !== 'all') {
            result = result.filter((lcat)=>lcat.teamName === filters.team);
        }
        // Apply sort
        result.sort((a, b)=>{
            let aValue;
            let bValue;
            switch(sortField){
                case 'code':
                    aValue = a.code || '';
                    bValue = b.code || '';
                    break;
                case 'title':
                    aValue = a.title || '';
                    bValue = b.title || '';
                    break;
                case 'team':
                    aValue = a.teamName || '';
                    bValue = b.teamName || '';
                    break;
                case 'category':
                    aValue = a.category || '';
                    bValue = b.category || '';
                    break;
                case 'clin':
                    aValue = a.clinLinkages?.[0]?.clinNumber || a.clinNumber || a.category || '';
                    bValue = b.clinLinkages?.[0]?.clinNumber || b.clinNumber || b.category || '';
                    break;
                case 'level':
                    aValue = a.level || '';
                    bValue = b.level || '';
                    break;
                case 'hours':
                    aValue = a.estimatedHours || 0;
                    bValue = b.estimatedHours || 0;
                    break;
                case 'rate':
                    aValue = a.baseHourlyRate || 0;
                    bValue = b.baseHourlyRate || 0;
                    break;
                default:
                    aValue = a.code || '';
                    bValue = b.code || '';
            }
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
            }
            return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
        });
        return result;
    }, [
        lcats,
        filters,
        sortField,
        sortOrder
    ]);
    const SortIcon = ({ field })=>{
        if (sortField !== field) return null;
        return sortOrder === 'asc' ? /*#__PURE__*/ _jsx(ArrowUp, {
            className: "w-4 h-4 ml-1 inline"
        }) : /*#__PURE__*/ _jsx(ArrowDown, {
            className: "w-4 h-4 ml-1 inline"
        });
    };
    // Get confidence badge color
    const getConfidenceBadge = (confidence)=>{
        if (confidence == null) return /*#__PURE__*/ _jsx("span", {
            className: "text-gray-400",
            children: "-"
        });
        const percentage = (confidence * 100).toFixed(0);
        if (confidence >= 0.9) {
            return /*#__PURE__*/ _jsxs(Badge, {
                className: "bg-green-100 text-green-700 text-xs",
                variant: "secondary",
                children: [
                    percentage,
                    "%"
                ]
            });
        }
        if (confidence >= 0.7) {
            return /*#__PURE__*/ _jsxs(Badge, {
                className: "bg-yellow-100 text-yellow-700 text-xs",
                variant: "secondary",
                children: [
                    percentage,
                    "%"
                ]
            });
        }
        return /*#__PURE__*/ _jsxs(Badge, {
            className: "bg-orange-100 text-orange-700 text-xs",
            variant: "secondary",
            children: [
                percentage,
                "%"
            ]
        });
    };
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-200 bg-gray-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 md:grid-cols-3 gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "relative",
                            children: [
                                /*#__PURE__*/ _jsx(Search, {
                                    className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    type: "text",
                                    placeholder: "Search labor categories...",
                                    value: filters.search,
                                    onChange: (e)=>setFilters({
                                            ...filters,
                                            search: e.target.value
                                        }),
                                    className: "pl-10"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("select", {
                            value: filters.category,
                            onChange: (e)=>setFilters({
                                    ...filters,
                                    category: e.target.value
                                }),
                            className: "px-3 py-2 border border-gray-300 rounded-md text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "all",
                                    children: "All Categories"
                                }),
                                categories.map((category)=>/*#__PURE__*/ _jsx("option", {
                                        value: category,
                                        children: category
                                    }, category))
                            ]
                        }),
                        teams.length > 0 && /*#__PURE__*/ _jsxs("select", {
                            value: filters.team,
                            onChange: (e)=>setFilters({
                                    ...filters,
                                    team: e.target.value
                                }),
                            className: "px-3 py-2 border border-gray-300 rounded-md text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "all",
                                    children: "All Teams"
                                }),
                                teams.map((team)=>/*#__PURE__*/ _jsx("option", {
                                        value: team,
                                        children: team
                                    }, team))
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto",
                children: /*#__PURE__*/ _jsxs("table", {
                    className: "w-full",
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            className: "bg-gray-50 border-b border-gray-200",
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('code'),
                                        children: [
                                            "Code",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "code"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('title'),
                                        children: [
                                            "Title",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "title"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('team'),
                                        children: [
                                            "Team",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "team"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('clin'),
                                        children: [
                                            "CLIN",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "clin"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('level'),
                                        children: [
                                            "Level",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "level"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Clearance"
                                    }),
                                    /*#__PURE__*/ _jsxs("th", {
                                        className: "px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100",
                                        onClick: ()=>handleSort('rate'),
                                        children: [
                                            "Hourly Rate",
                                            /*#__PURE__*/ _jsx(SortIcon, {
                                                field: "rate"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        className: "px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider",
                                        children: "Confidence"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            className: "bg-white divide-y divide-gray-200",
                            children: filteredAndSortedLCATs.map((lcat)=>/*#__PURE__*/ _jsxs("tr", {
                                    onClick: ()=>onRowClick(lcat),
                                    "data-testid": `${Testids.lcat.tableRow}-${lcat.code}`,
                                    className: "hover:bg-gray-50 cursor-pointer transition-colors",
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 text-sm font-medium text-gray-900",
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center gap-1.5",
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "max-w-[120px] truncate",
                                                        children: lcat.code
                                                    }),
                                                    lcat.isKeyPersonnel && /*#__PURE__*/ _jsx("span", {
                                                        title: "Key Personnel",
                                                        children: /*#__PURE__*/ _jsx(Star, {
                                                            className: "w-3.5 h-3.5 text-purple-500 flex-shrink-0"
                                                        })
                                                    })
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 text-sm text-gray-900",
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: "max-w-xs truncate",
                                                children: lcat.title
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm",
                                            children: lcat.teamName ? /*#__PURE__*/ _jsx(Badge, {
                                                className: "bg-indigo-100 text-indigo-700 text-xs",
                                                variant: "secondary",
                                                children: lcat.teamName
                                            }) : /*#__PURE__*/ _jsx("span", {
                                                className: "text-gray-400",
                                                children: "-"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono",
                                            children: lcat.clinLinkages?.length ? lcat.clinLinkages.map((cl)=>cl.clinNumber).join(', ') : lcat.clinNumber || lcat.category || '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500",
                                            children: lcat.level || lcat.education?.level || '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm",
                                            children: lcat.clearance && lcat.clearance !== 'none' ? /*#__PURE__*/ _jsxs(Badge, {
                                                className: `text-xs ${clearanceColors[lcat.clearance] || 'bg-gray-100 text-gray-700'}`,
                                                variant: "secondary",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Shield, {
                                                        className: "w-3 h-3 mr-1"
                                                    }),
                                                    clearanceLabels[lcat.clearance] || lcat.clearance
                                                ]
                                            }) : /*#__PURE__*/ _jsx("span", {
                                                className: "text-gray-400",
                                                children: "-"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right",
                                            "data-testid": `${Testids.lcat.tableRowRateCell}-${lcat.code}`,
                                            children: lcat.baseHourlyRate ? `$${lcat.baseHourlyRate.toLocaleString()}` : '-'
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            className: "px-6 py-4 whitespace-nowrap text-sm text-center",
                                            children: getConfidenceBadge(lcat.confidence)
                                        })
                                    ]
                                }, lcat.code))
                        })
                    ]
                })
            }),
            filteredAndSortedLCATs.length === 0 && /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-12 text-center text-gray-500",
                children: "No labor categories match the current filters"
            })
        ]
    });
});
